<template>
  <div class="app-container">
    <div class="filter-container">
      <el-date-picker v-model="query.year" type="year" placeholder="年份" value-format="yyyy" class="filter-item" style="width: 110px;" />
      <el-select v-model="query.season" placeholder="季度" class="filter-item" style="width: 100px;">
        <el-option v-for="s in 4" :key="s" :label="'第' + s + '季度'" :value="s" />
      </el-select>
      <el-button v-waves class="filter-item" type="primary" icon="el-icon-search" @click="loadTasks">
        查询
      </el-button>
      <span class="filter-item" style="color: #909399; font-size: 13px;">
        待评 {{ pendingCount }} / 共 {{ tasks.length }} 项
      </span>
    </div>

    <el-alert :type="periodStatus.type" :closable="false" :title="periodStatus.text" style="margin-bottom: 15px;" />

    <el-table
      v-loading="loading"
      :data="visibleTasks"
      border
      fit
      highlight-current-row
      style="width: 100%;"
    >
      <el-table-column label="评分场景" min-width="120" align="center">
        <template slot-scope="{row}">
          <el-tag :type="sceneTag(row.scene)" size="medium">{{ row.sceneName }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="被评对象" min-width="160" align="center">
        <template slot-scope="{row}">
          <span v-if="row.targetType === 'DEPT'">{{ row.deptName }}</span>
          <span v-else>{{ row.empName }}<span v-if="row.deptName" style="color:#909399">（{{ row.deptName }}）</span></span>
        </template>
      </el-table-column>
      <el-table-column label="岗位" min-width="120" align="center">
        <template slot-scope="{row}">
          <span>{{ (row.stationNames || []).join('、') }}</span>
        </template>
      </el-table-column>
      <el-table-column label="状态" width="90" align="center">
        <template slot-scope="{row}">
          <el-tag :type="row.submitted ? 'success' : 'info'">{{ row.submitted ? '已评' : '待评' }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="已评总分" width="100" align="center">
        <template slot-scope="{row}">
          <span>{{ row.totalScore !== null && row.totalScore !== undefined ? row.totalScore : '' }}</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="110" align="center" class-name="small-padding fixed-width">
        <template slot-scope="{row}">
          <el-button :type="scoreBtnType(row)" size="mini" @click="openScore(row)">
            {{ scoreBtnText(row) }}
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 评分弹窗 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="1200px" top="4vh" :close-on-click-modal="false" :append-to-body="false" @open="resetDialogScroll">
      <div v-if="currentTask">
        <el-alert v-if="!periodOpen" type="warning" :closable="false" style="margin-bottom: 12px;"
          title="该期次考核已关闭或未开启，仅可查看评分明细，不能提交。" />
        <!-- 高管评部门：被评部门定量数据只读参考（事实依据，不含他人评分） -->
        <el-alert v-if="currentTask.scene === 'EXEC_DEPT' && currentTask.quant" type="info" :closable="false" style="margin-bottom: 12px;"
          :title="'定量数据参考：' + quantRefText(currentTask.quant)" />
        <el-alert v-else-if="currentTask.scene === 'EXEC_DEPT'" type="warning" :closable="false" style="margin-bottom: 12px;"
          title="该部门本季度尚未录入定量数据，请结合部门实际情况评分。" />
        <!-- 高管加减分：无打分表 -->
        <template v-if="currentTask.scene === 'EXEC_ADJUST'">
          <el-alert type="info" :closable="false" style="margin-bottom: 15px;"
            title="公司高管可对各部门除上述工作内容以外的情况进行加减分，其结果直接在部门加权后的最终得分上进行加减，每位高管加减分不超过1分，须写明理由。" />
          <el-form label-width="100px">
            <el-form-item label="加减分" required>
              <el-input-number v-model="adjustValue" :min="-1" :max="1" :step="0.1" :precision="1" />
              <span style="margin-left: 10px; color: #909399;">-1 ~ +1 分</span>
            </el-form-item>
            <el-form-item label="理由" required>
              <el-input v-model="reason" type="textarea" :rows="3" placeholder="加减分必须写明理由" />
            </el-form-item>
          </el-form>
        </template>

        <!-- 常规打分表 -->
        <template v-else>
          <el-form label-width="100px" style="margin-bottom: 5px;">
            <el-form-item v-if="candidateForms.length > 1" label="评分表">
              <el-select v-model="selectedFormKey" style="width: 420px;" @change="onFormChange">
                <el-option v-for="f in candidateForms" :key="f.key" :label="f.title" :value="f.key" />
              </el-select>
            </el-form-item>
          </el-form>

          <el-table :data="flatScoreRows" border class="score-table" :span-method="scoreSpanMethod" style="margin-bottom: 18px;">
            <el-table-column label="类别" width="170">
              <template slot-scope="{row}">
                {{ row.groupName }}<span v-if="currentForm.mode === 'GROUP_WEIGHT'" style="color: #909399; font-weight: normal; font-size: 12px;">（组内得分{{ row.groupNorm ? '换算百分制后' : '' }}×{{ row.groupWeight }}计入总分）</span>
              </template>
            </el-table-column>
            <el-table-column label="考核指标" min-width="110">
              <template slot-scope="{row}">{{ row.sub }}</template>
            </el-table-column>
            <el-table-column label="指标要求" min-width="300">
                <template slot-scope="{row}"><span style="white-space: pre-wrap;">{{ row.req }}</span></template>
              </el-table-column>
              <el-table-column v-if="currentForm.showWeight" label="权重" width="70" align="center">
                <template slot-scope="{row}">{{ row.max }}%</template>
              </el-table-column>
              <el-table-column label="评分规则" width="200">
                <template slot-scope="{row}">
                  <span style="white-space: pre-line;">{{ formatRule(row.r) }}</span>
                </template>
              </el-table-column>
              <el-table-column label="得分" width="170" align="center">
                <template slot-scope="{row}">
                  <el-input-number v-model="scores[row._gi + '-' + row._ii]" :min="0" :max="row.max" :step="1" :precision="0" controls-position="right" style="width: 140px;" @input.native="onScoreKeystroke(row, $event)" />
                  <div class="score-max">满分 {{ row.max }}</div>
                </template>
              </el-table-column>
          </el-table>

          <el-alert v-if="currentForm.formula" type="info" :closable="false" :title="currentForm.formula" style="margin-bottom: 12px;" />
          <el-alert v-if="currentForm.note" type="warning" :closable="false" :title="currentForm.note" style="margin-bottom: 12px;" />
          <el-alert v-if="reasonRequired" type="error" :closable="false" style="margin-bottom: 12px;"
            title="当前评分高于95分（含）或低于70分，必须写明原因，否则视为无效分。" />

          <el-form label-width="100px">
            <el-form-item label="总分">
              <span class="total-score">{{ currentTotal }}</span>
            </el-form-item>
            <el-form-item label="原因">
              <el-input v-model="reason" type="textarea" :rows="2" :placeholder="reasonRequired ? '必填：95分以上或70分以下须写明原因' : '选填'" />
            </el-form-item>
          </el-form>

          <!-- 高管加减分：整合在评部门弹窗内，与打分一次提交（EXEC_ADJUST 场景单独存行，计算口径不变） -->
          <div v-if="currentTask.scene === 'EXEC_DEPT'" class="adjust-section">
            <div class="adjust-title">高管加减分
              <span class="adjust-tip">对除工作内容以外的情况进行加减分，直接计入部门最终得分；每位高管不超过 ±1 分，非 0 须写明理由</span>
            </div>
            <el-form label-width="100px" style="margin-bottom: 0;">
              <el-form-item label="加减分">
                <el-input-number v-model="adjustValue" :min="-1" :max="1" :step="0.1" :precision="1" />
                <span class="adjust-range">-1 ~ +1 分，0 为不加不减</span>
              </el-form-item>
              <el-form-item label="理由" :required="adjustValue !== 0">
                <el-input v-model="adjustReason" type="textarea" :rows="2" placeholder="加减分非 0 时必须写明理由" />
              </el-form-item>
            </el-form>
          </div>
        </template>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{ periodOpen ? '取消' : '关闭' }}</el-button>
        <el-button v-if="periodOpen" type="primary" :loading="submitting" @click="submit">提交</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getMyTasks, getPeriods, submitScore } from '../../api/performance'
import { matchForms, calcTotal, formByKey } from './forms'
import waves from '../../directive/waves'

export default {
  name: 'PerfMyTasks',
  directives: { waves },
  data() {
    const now = new Date()
    return {
      query: {
        year: String(now.getFullYear()),
        season: Math.floor(now.getMonth() / 3) + 1
      },
      tasks: [],
      periods: [],
      loading: false,
      dialogVisible: false,
      submitting: false,
      currentTask: null,
      candidateForms: [],
      selectedFormKey: '',
      scores: {},
      reason: '',
      adjustValue: 0,
      adjustReason: '',
      adjustTask: null
    }
  },
  computed: {
    /** 当前所选期次是否处于开启状态（开启才能提交/修改评分） */
    periodOpen() {
      const p = this.periods.find(x => Number(x.year) === Number(this.query.year) && Number(x.season) === Number(this.query.season))
      return !!p && p.enabled === 1
    },
    /** 当前所选期次的状态横幅 */
    periodStatus() {
      const p = this.periods.find(x => Number(x.year) === Number(this.query.year) && Number(x.season) === Number(this.query.season))
      if (!p) return { type: 'info', text: '该期次尚未开启考核（请联系管理员在【考核配置】中开启），无法提交评分' }
      if (p.enabled === 1) return { type: 'success', text: '考核进行中：当前期次可提交评分' }
      return { type: 'warning', text: '该期次考核已关闭：仅可查看已评记录，无法提交评分' }
    },
    pendingCount() {
      return this.visibleTasks.filter(t => !t.submitted).length
    },
    /** 高管加减分已整合进"高管评部门"弹窗，任务列表不再单独展示 EXEC_ADJUST 行 */
    visibleTasks() {
      return this.tasks.filter(t => t.scene !== 'EXEC_ADJUST')
    },
    currentForm() {
      return this.candidateForms.find(f => f.key === this.selectedFormKey) || null
    },
    currentTotal() {
      return calcTotal(this.currentForm, this.scores)
    },
    /** 打分表摊平为行（带类别/子组合并行信息），供纸质表样式合并单元格排版；得分绑定仍用 _gi-_ii */
    flatScoreRows() {
      if (!this.currentForm) return []
      const rows = []
      this.currentForm.groups.forEach((g, gi) => {
        g.items.forEach((it, ii) => {
          const dash = (it.n || '').indexOf('-')
          rows.push(Object.assign({}, it, {
            _gi: gi, _ii: ii,
            groupName: g.name, groupWeight: g.weight, groupNorm: !!g.norm,
            sub: dash > 0 ? it.n.slice(0, dash) : it.n
          }))
        })
      })
      let i = 0
      this.currentForm.groups.forEach(g => {
        rows[i]._groupSpan = g.items.length
        for (let k = i + 1; k < i + g.items.length; k++) rows[k]._groupSpan = 0
        i += g.items.length
      })
      let s = 0
      while (s < rows.length) {
        let e = s + 1
        while (e < rows.length && rows[e]._gi === rows[s]._gi && rows[e].sub === rows[s].sub) e++
        rows[s]._subSpan = e - s
        for (let k = s + 1; k < e; k++) rows[k]._subSpan = 0
        s = e
      }
      return rows
    },
    reasonRequired() {
      // 95分（含）以上/70分以下必填原因：部门内打分场景（负责人评部门内员工、部门内员工互评）与全员互评
      if (!this.currentTask) return false
      const sc = this.currentTask.scene
      if (sc !== 'LEADER_EMP' && sc !== 'EMP_PEER' && sc !== 'ALL_PEER') return false
      return this.currentTotal >= 95 || this.currentTotal < 70
    },
    dialogTitle() {
      if (!this.currentTask) return ''
      const t = this.currentTask
      const target = t.targetType === 'DEPT' ? t.deptName : t.empName
      return t.sceneName + ' - ' + target
    }
  },
  created() {
    // 先取考核期列表：存在开启中的期次时，默认选中它
    getPeriods().then(response => {
      this.periods = response.data || []
      const open = this.periods.find(p => p.enabled === 1)
      if (open) {
        this.query.year = String(open.year)
        this.query.season = open.season
      }
      this.loadTasks()
    }).catch(() => {
      this.loadTasks()
    })
  },
  methods: {
    /** 需打分表的场景未配置 → 按钮提示未配置（全场景严格模式，无兜底） */
    unconfigured(row) {
      if (row.submitted) return false
      if (row.scene === 'LEADER_EMP' || row.scene === 'EMP_PEER') return !row.kpiFormKey
      if (row.scene === 'EXEC_DEPT' || row.scene === 'LEADER_DEPT' || row.scene === 'EMP_LEADER' || row.scene === 'ALL_PEER') return !row.confFormKey
      return false
    },
    /** 打分表未配置/不存在时的错误文案（全场景严格模式） */
    noFormMsg(task) {
      if (task.scene === 'LEADER_EMP' || task.scene === 'EMP_PEER') {
        return task.kpiFormKey
          ? '该员工岗位配置的考核表不存在，请联系管理员在【岗位管理】中检查'
          : '该员工岗位未配置绩效考核表（部门内互评与负责人评员工同表），请联系管理员在【岗位管理】中配置后再评分'
      }
      if (task.scene === 'ALL_PEER') {
        return task.confFormKey
          ? '该员工岗位配置的全员互评用表不存在，请联系管理员在【岗位管理】中检查'
          : '该员工岗位未配置全员互评用表，请联系管理员在【岗位管理】中配置后再评分'
      }
      if (task.scene === 'EMP_LEADER') {
        return task.confFormKey
          ? '该负责人岗位配置的负责人考核表不存在，请联系管理员在【岗位管理】中检查'
          : '该负责人岗位未配置负责人考核表，请联系管理员在【岗位管理】中配置后再评分'
      }
      return task.confFormKey
        ? '被评部门配置的打分表不存在，请联系管理员在【部门管理】中检查'
        : '被评部门未配置打分表，请联系管理员在【部门管理】中配置后再评分'
    },
    scoreBtnText(row) {
      if (this.unconfigured(row)) return '未配置'
      if (!this.periodOpen) return row.submitted ? '查看' : '已关闭'
      return row.submitted ? '修改' : '评分'
    },
    scoreBtnType(row) {
      if (this.unconfigured(row)) return 'info'
      if (!this.periodOpen) return 'info'
      return row.submitted ? 'success' : 'primary'
    },
    sceneTag(scene) {
      const map = {
        EXEC_DEPT: 'danger',
        EXEC_ADJUST: 'warning',
        LEADER_DEPT: '',
        LEADER_EMP: 'success',
        EMP_LEADER: 'success',
        EMP_PEER: 'info',
        ALL_PEER: 'info'
      }
      return map[scene] || 'info'
    },
    /** 高管评部门弹窗的定量参考文案（空值显示 —） */
    quantRefText(q) {
      const f = v => (v === null || v === undefined ? '—' : v)
      let s = q.scope === 'DEPT' ? '【部门口径】' : (q.scope === 'COMPANY' ? '【公司整体口径】' : '')
      s += '新增不良贷款 目标 ' + f(q.aTarget) + ' / 实际 ' + f(q.aActual) + '，得分 ' + f(q.aScore) + ' 分'
      s += '；不良贷款余额 目标 ' + f(q.bTarget) + ' / 实际 ' + f(q.bActual) + '，得分 ' + f(q.bScore) + ' 分'
      if (q.aWeight !== null && q.aWeight !== undefined) s += '；占比 ' + q.aWeight + '%/' + q.bWeight + '%'
      if (q.score !== null && q.score !== undefined) s += '；定量得分 ' + q.score
      return s
    },
    formatRule(r) {
      if (!r) return ''
      return r.split('|').map(s => (s.endsWith('分') ? s : s + '分')).join('\n')
    },
    /** 合并单元格规则：第0列按类别(组)合并、第1列按同组内连续相同考核指标(子组)合并 */
    scoreSpanMethod({ rowIndex, columnIndex }) {
      const row = this.flatScoreRows[rowIndex]
      if (!row) return [1, 1]
      if (columnIndex === 0) return [row._groupSpan, 1]
      if (columnIndex === 1) return [row._subSpan, 1]
      return [1, 1]
    },
    loadTasks() {
      if (!this.query.year || !this.query.season) {
        this.$message.warning('请选择年份和季度')
        return
      }
      this.loading = true
      getMyTasks(this.query.year, this.query.season).then(response => {
        this.tasks = response.data || []
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
      // 同步刷新考核期状态（横幅与按钮禁用实时跟随管理员的开启/关闭操作）
      getPeriods().then(response => {
        this.periods = response.data || []
      }).catch(() => {})
    },
    openScore(task) {
      if (!this.periodOpen && !task.submitted) {
        this.$message.warning('该期次考核未开启或已关闭，无法评分')
        return
      }
      this.currentTask = task
      this.scores = {}
      this.reason = task.reason || ''
      // 高管评部门：定位同部门的加减分任务并预填其已评分值/理由（整合后一次提交两个场景）
      this.adjustTask = task.scene === 'EXEC_DEPT'
        ? (this.tasks.find(t => t.scene === 'EXEC_ADJUST' && t.deptId === task.deptId) || null)
        : null
      this.adjustValue = 0
      this.adjustReason = ''
      if (task.scene === 'EXEC_ADJUST') {
        this.adjustValue = task.totalScore !== null && task.totalScore !== undefined ? Number(task.totalScore) : 0
        this.adjustReason = task.reason || ''
        this.candidateForms = []
        this.selectedFormKey = ''
        this.dialogVisible = true
        return
      }
      if (this.adjustTask) {
        this.adjustValue = this.adjustTask.totalScore !== null && this.adjustTask.totalScore !== undefined ? Number(this.adjustTask.totalScore) : 0
        this.adjustReason = this.adjustTask.reason || ''
      }
      // 先解析已评明细（可能含上次所用表）
      let savedFormKey = ''
      let savedScores = null
      if (task.items) {
        try {
          const saved = JSON.parse(task.items)
          if (saved.formKey) savedFormKey = saved.formKey
          if (saved.scores) savedScores = Object.assign({}, saved.scores)
          if (saved.reason && !this.reason) this.reason = saved.reason
        } catch (e) { /* 忽略损坏数据 */ }
      }
      this.candidateForms = matchForms(task)
      // 岗位配置缺失但历史已评过：用上次所用表回显（允许查看/修改）
      if (this.candidateForms.length === 0 && savedFormKey) {
        const savedForm = formByKey(savedFormKey)
        if (savedForm) this.candidateForms = [savedForm]
      }
      if (this.candidateForms.length === 0) {
        this.$message.error(this.noFormMsg(task))
        return
      }
      this.selectedFormKey = this.candidateForms.some(f => f.key === savedFormKey)
        ? savedFormKey
        : this.candidateForms[0].key
      this.prefillScores(savedScores)
      this.dialogVisible = true
    },
    /** 弹窗每次打开把内容区滚动重置到顶部：body 限高滚动，重开会保留上次滚动位置，导致顶部评分项被跳过看不到 */
    resetDialogScroll() {
      const reset = () => {
        const body = this.$el.querySelector('.el-dialog__body')
        if (body) body.scrollTop = 0
      }
      reset()
      this.$nextTick(reset)
    },
    onFormChange() {
      this.prefillScores()
    },
    /** 预建当前所选表全部评分项键为反应式键：避免 el-input-number 的 v-model 新建键不触发响应式，导致总分长时间不刷新、与提交明细脱钩 */
    prefillScores(base) {
      const form = this.candidateForms.find(f => f.key === this.selectedFormKey) || null
      const scores = {}
      if (form) {
        form.groups.forEach((g, gi) => g.items.forEach((it, ii) => { scores[gi + '-' + ii] = undefined }))
      }
      Object.assign(scores, base || {})
      this.scores = scores
    },
    /**
     * el-input-number 只在失焦/回车时同步 v-model，键入过程中总分不跟随；
     * 这里监听原生 input 事件实时写入得分（限幅 0~该项满分），使总分随键入即时更新。
     * 失焦时组件仍会按自身规则归一化并回写 v-model，两者不冲突。
     */
    onScoreKeystroke(row, e) {
      const raw = e.target.value
      const key = row._gi + '-' + row._ii
      if (raw === '' || raw === '-') {
        this.$set(this.scores, key, undefined)
        return
      }
      const n = Number(raw)
      if (isNaN(n)) return
      this.$set(this.scores, key, Math.min(Math.max(n, 0), row.max))
    },
    submit() {
      const t = this.currentTask
      if (!t) return
      const data = {
        year: Number(this.query.year),
        season: this.query.season,
        scene: t.scene,
        deptId: t.targetType === 'DEPT' ? t.deptId : null,
        empId: t.targetType === 'EMP' ? t.empId : null
      }
      if (t.scene === 'EXEC_ADJUST') {
        data.totalScore = this.adjustValue
        data.reason = this.reason
        if (!this.reason || !this.reason.trim()) {
          this.$message.warning('加减分必须写明理由')
          return
        }
      } else {
        if (!this.currentForm) return
        // 总分当场现算（不用 computed 缓存）：杜绝个别写入路径不触发响应式刷新时总分与明细脱钩
        const freshTotal = calcTotal(this.currentForm, this.scores)
        data.totalScore = freshTotal
        data.reason = this.reason
        if ((freshTotal >= 95 || freshTotal < 70) && (!this.reason || !this.reason.trim())) {
          this.$message.warning('评分高于95分（含）或低于70分，必须写明原因')
          return
        }
        // 高管评部门：整合的加减分非 0 时必须写明理由
        if (t.scene === 'EXEC_DEPT' && this.adjustValue !== 0 && (!this.adjustReason || !this.adjustReason.trim())) {
          this.$message.warning('加减分必须写明理由')
          return
        }
        data.items = JSON.stringify({
          formKey: this.selectedFormKey,
          formTitle: this.currentForm.title,
          scores: this.scores
        })
      }
      this.submitting = true
      let chain = submitScore(data)
      // 高管评部门：同一次提交里附带加减分（EXEC_ADJUST 场景单独存行，计算口径不变；0 分表示不加不减）
      if (t.scene === 'EXEC_DEPT' && this.adjustTask) {
        chain = chain.then(() => submitScore({
          year: data.year,
          season: data.season,
          scene: 'EXEC_ADJUST',
          deptId: t.deptId,
          empId: null,
          totalScore: this.adjustValue,
          reason: this.adjustReason
        }))
      }
      chain.then(() => {
        this.submitting = false
        this.dialogVisible = false
        this.$notify({ title: '成功', message: '评分已保存', type: 'success', duration: 2000 })
        this.loadTasks()
      }).catch(() => {
        this.submitting = false
      })
    }
  }
}
</script>

<style scoped>
/* ===== 轻量视觉优化（沿用项目 element-ui 原有配色，不引入新色系） ===== */
::v-deep .el-table th {
  background: #f5f7fa !important;
  color: #606266;
  font-weight: 600;
}
::v-deep .el-table tbody tr:hover > td {
  background: #ecf5ff !important;
}
::v-deep .el-button--primary {
  border-radius: 4px;
}
/* 打分弹窗：圆角与内容限高，标题沿用默认白底 */
::v-deep .el-dialog {
  border-radius: 8px;
}
::v-deep .el-dialog__title {
  font-size: 18px;
  font-weight: 600;
}
::v-deep .el-dialog__body {
  padding: 20px 24px;
  max-height: 78vh;
  overflow-y: auto;
}
/* 打分表分组标题：主题蓝竖条 */
.form-group-title {
  font-size: 15px;
  font-weight: bold;
  margin-bottom: 10px;
  border-left: 3px solid #409EFF;
  padding-left: 8px;
  line-height: 1.3;
}
/* 总分：主题蓝加粗 */
.total-score {
  font-size: 26px;
  font-weight: bold;
  color: #409EFF;
}
/* 高管加减分整合区：浅灰底 + 虚线边框，与原弹窗区分但不引入新色 */
.adjust-section {
  margin-top: 6px;
  padding: 14px 15px 4px;
  background: #fafafa;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
}
.adjust-title {
  font-weight: bold;
  margin-bottom: 10px;
  border-left: 3px solid #E6A23C;
  padding-left: 8px;
  line-height: 1.3;
}
.adjust-tip {
  margin-left: 8px;
  font-size: 12px;
  font-weight: normal;
  color: #909399;
}
.adjust-range {
  margin-left: 10px;
  font-size: 12px;
  color: #909399;
}
/* 打分表与弹窗内字号放大 */
::v-deep .score-table {
  font-size: 14px;
}
.score-max {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
}
::v-deep .el-dialog .el-alert__title {
  font-size: 14px;
}
::v-deep .el-dialog .el-input-number__inner,
::v-deep .el-dialog .el-textarea__inner {
  font-size: 14px;
}
</style>

